import type { KeyboardEvent } from 'react';
import { DEFAULT_FORM_INPUT_TYPES, ITEM_STATUS_CODES, OPERATOR_RESPONSE_TYPES } from '../../utils/constants';
import { getOperatorResponseChoices, requiresNotesOrMedia } from '../../utils/formPromptMapping';
import {
    canAdvanceDailyServiceNewIssues,
    getDailyServiceNewIssuesBlockedReason,
    isDailyServiceNewIssuesPrompt,
} from '../../utils/dailyServiceNewIssues';
import { isReportUMCounterPromptLabel } from '../../utils/reportUtils';
import type { tInspectionFormInput } from '../../types';
import type { tMediaInput } from '../../types';

export function isUmCounterStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion &&
        isReportUMCounterPromptLabel(prompt.label)
    );
}

export function isFourResponseStep(prompt: tInspectionFormInput | undefined): boolean {
    if (!prompt || prompt.type !== DEFAULT_FORM_INPUT_TYPES.section) {
        return false;
    }

    return (
        !prompt.subType ||
        prompt.subType === DEFAULT_FORM_INPUT_TYPES.fourResponse ||
        prompt.subType === OPERATOR_RESPONSE_TYPES.FOUR_BUTTON
    );
}

export function isYesNoStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.section &&
        prompt.subType === DEFAULT_FORM_INPUT_TYPES.yesNo
    );
}

export function isCheckboxStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.section &&
        prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox
    );
}

export function isSectionQuestionStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.section &&
        prompt.subType === DEFAULT_FORM_INPUT_TYPES.sectionQuestion
    );
}

export function isDropdownStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.section &&
        prompt.subType === DEFAULT_FORM_INPUT_TYPES.dropdown
    );
}

export function isOperatorResponseStep(prompt: tInspectionFormInput | undefined): boolean {
    if (!prompt || prompt.type !== DEFAULT_FORM_INPUT_TYPES.section) {
        return false;
    }

    return Boolean(getOperatorResponseChoices(prompt.subType));
}

export function isQuantityOnlyStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.section &&
        prompt.subType === OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY
    );
}

export function isAcknowledgementStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.section &&
        prompt.subType === OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT
    );
}

export function isNewIssuesStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.section && isDailyServiceNewIssuesPrompt(prompt)
    );
}

export function isStandAloneFieldStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        prompt?.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion &&
        !isReportUMCounterPromptLabel(prompt.label)
    );
}

export function isFlowWizardNativeStep(prompt: tInspectionFormInput | undefined): boolean {
    return (
        isUmCounterStep(prompt) ||
        isFourResponseStep(prompt) ||
        isYesNoStep(prompt) ||
        isCheckboxStep(prompt) ||
        isSectionQuestionStep(prompt) ||
        isDropdownStep(prompt) ||
        isOperatorResponseStep(prompt) ||
        isQuantityOnlyStep(prompt) ||
        isAcknowledgementStep(prompt) ||
        isNewIssuesStep(prompt) ||
        isStandAloneFieldStep(prompt)
    );
}

export function canAdvanceFlowStep(
    prompt: tInspectionFormInput | undefined,
    media: { [key: number]: tMediaInput[] } | null,
    activeStepIndex: number,
): boolean {
    if (!prompt) {
        return false;
    }

    if (isNewIssuesStep(prompt)) {
        return canAdvanceDailyServiceNewIssues(prompt, media?.[activeStepIndex] ?? []);
    }

    if (isQuantityOnlyStep(prompt)) {
        return true;
    }

    const requiresValue =
        prompt.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
        prompt.subType !== DEFAULT_FORM_INPUT_TYPES.dropdown &&
        prompt.subType !== OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT &&
        prompt.subType !== OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY;

    if (requiresValue && !prompt.value) {
        return false;
    }

    if (requiresNotesOrMedia(prompt)) {
        const hasNotes = Boolean(prompt.notes && prompt.notes.length > 0);
        const hasMedia = Boolean(media && media[activeStepIndex] && media[activeStepIndex].length > 0);
        if (!(hasNotes || hasMedia)) {
            return false;
        }
    } else if (
        (prompt.value === ITEM_STATUS_CODES.warn || prompt.value === ITEM_STATUS_CODES.inop) &&
        !(
            (prompt.notes && prompt.notes.length > 0) ||
            (media && media[activeStepIndex] && media[activeStepIndex].length > 0)
        )
    ) {
        return false;
    }

    return true;
}

export function shouldAutoAdvanceFlowSelection(
    prompt: tInspectionFormInput | undefined,
    value: string,
): boolean {
    if (!prompt) {
        return false;
    }

    if (isFourResponseStep(prompt) && shouldAutoAdvanceFourResponse(value)) {
        return true;
    }

    const choices = getOperatorResponseChoices(prompt.subType);
    const choice = choices?.find((entry) => entry.value === value);
    if (!choice?.autoAdvance) {
        return false;
    }

    return !requiresNotesOrMedia({ ...prompt, value });
}

export function getWizardProgressPercent(activeStepIndex: number, totalSteps: number): number {
    if (totalSteps <= 0) {
        return 0;
    }

    return Math.round((activeStepIndex / totalSteps) * 100);
}

export type WizardSectionMeta = {
    sectionTitle: string;
    sectionIndex: number;
    sectionCount: number;
    itemIndex: number;
    itemCount: number;
};

export function getWizardSectionMeta(
    prompts: tInspectionFormInput[],
    activeStepIndex: number,
): WizardSectionMeta | null {
    const current = prompts[activeStepIndex];
    if (!current || current.type !== DEFAULT_FORM_INPUT_TYPES.section || !current.title) {
        return null;
    }

    const sectionTitles: string[] = [];
    prompts.forEach((prompt) => {
        if (prompt.type === DEFAULT_FORM_INPUT_TYPES.section && prompt.title && !sectionTitles.includes(prompt.title)) {
            sectionTitles.push(prompt.title);
        }
    });

    const sectionTitle = current.title;
    const sectionIndex = Math.max(1, sectionTitles.indexOf(sectionTitle) + 1);
    const sectionItems = prompts
        .map((prompt, index) => ({ prompt, index }))
        .filter(({ prompt }) => prompt.type === DEFAULT_FORM_INPUT_TYPES.section && prompt.title === sectionTitle);
    const itemIndex = Math.max(
        1,
        sectionItems.findIndex(({ index }) => index === activeStepIndex) + 1,
    );

    return {
        sectionTitle,
        sectionIndex,
        sectionCount: Math.max(1, sectionTitles.length),
        itemIndex,
        itemCount: Math.max(1, sectionItems.length),
    };
}

export function getWizardProgressLeftLabel(
    prompts: tInspectionFormInput[],
    activeStepIndex: number,
    flowKind: 'inspection' | 'dailyService' = 'inspection',
): string {
    if (isUmCounterStep(prompts[activeStepIndex])) {
        return flowKind === 'dailyService' ? 'Starting Daily Service' : 'Starting Inspection';
    }

    const sectionMeta = getWizardSectionMeta(prompts, activeStepIndex);
    return sectionMeta?.sectionTitle ?? 'Inspection';
}

export function hasInspectionProgress(
    prompts: tInspectionFormInput[],
    activeStepIndex: number,
    shouldReviewForm: boolean,
    media: { [key: number]: { id?: number }[] } | null,
    audioReview: Record<number, Blob>,
): boolean {
    if (shouldReviewForm) {
        return true;
    }

    if (activeStepIndex > 0) {
        return true;
    }

    if (prompts.some((prompt) => Boolean(prompt.value || prompt.notes))) {
        return true;
    }

    if (media && Object.values(media).some((items) => items.length > 0)) {
        return true;
    }

    if (Object.keys(audioReview).length > 0) {
        return true;
    }

    return false;
}

/** OK and N/A advance immediately in production; warn/inop require notes/media + Next. */
export function shouldAutoAdvanceFourResponse(value: string): boolean {
    return value === ITEM_STATUS_CODES.ok || value === ITEM_STATUS_CODES.na;
}

export function getFlowStepBlockedReason(
    prompt: tInspectionFormInput | undefined,
    stepMediaItems: tMediaInput[],
): string | null {
    if (!prompt) {
        return 'Complete this step to continue.';
    }

    if (isNewIssuesStep(prompt)) {
        return getDailyServiceNewIssuesBlockedReason(prompt, stepMediaItems);
    }

    if (isQuantityOnlyStep(prompt)) {
        return null;
    }

    const requiresValue =
        prompt.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
        prompt.subType !== DEFAULT_FORM_INPUT_TYPES.dropdown &&
        prompt.subType !== OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT &&
        prompt.subType !== OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY;

    if (requiresValue && !prompt.value) {
        return 'Select a response to continue.';
    }

    const hasWarnInopEvidence =
        Boolean(prompt.notes && prompt.notes.length > 0) ||
        stepMediaItems.some((item) => item.data?.file);

    if (
        (prompt.value === ITEM_STATUS_CODES.warn || prompt.value === ITEM_STATUS_CODES.inop) &&
        !hasWarnInopEvidence
    ) {
        return 'Add notes or a photo to continue.';
    }

    return null;
}

export function createRadioGroupKeyDownHandler<T extends { value: string }>(
    options: readonly T[],
    onSelect: (value: string) => void,
): (event: KeyboardEvent<HTMLElement>) => void {
    return (event) => {
        if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
            return;
        }

        event.preventDefault();
        const radios = Array.from(
            event.currentTarget.querySelectorAll<HTMLButtonElement>('button[role="radio"]:not(:disabled)'),
        );
        if (!radios.length || !options.length) {
            return;
        }

        const currentIndex = Math.max(0, radios.indexOf(document.activeElement as HTMLButtonElement));
        const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
        const nextIndex = (currentIndex + delta + radios.length) % radios.length;
        const nextOption = options[nextIndex];
        if (!nextOption) {
            return;
        }

        onSelect(nextOption.value);
        radios[nextIndex]?.focus();
    };
}
